"use client";

import Link from "next/link";
import NextImage from "next/image";
import { HiLocationMarker, HiPhone, HiMail, HiArrowUp } from "react-icons/hi";
import { FaInstagram, FaTiktok, FaYoutube, FaWhatsapp } from "react-icons/fa";
import { useLanguage } from "@/lib/i18n";

const PARTNERS = [
  { name: "Traveloka", url: "/images/partners/traveloka-logo.webp", height: "h-6", link: "https://www.traveloka.com" },
  { name: "Tiket.com", url: "/images/partners/tiket-com-logo.webp", height: "h-5", link: "https://www.tiket.com" },
  { name: "Agoda", url: "/images/partners/agoda-logo.webp", height: "h-6", link: "https://www.agoda.com" },
  { name: "Booking.com", url: "/images/partners/booking-com-logo.webp", height: "h-4", link: "https://www.booking.com" },
  { name: "Airbnb", url: "/images/partners/aibnb-logo.webp", height: "h-5", link: "https://www.airbnb.com" },
  { name: "Expedia", url: "/images/partners/expedia-logo.webp", height: "h-5", link: "https://www.expedia.com" },
  { name: "Trip.com", url: "/images/partners/trip-com-logo.webp", height: "h-5", link: "https://www.trip.com" },
  { name: "TripAdvisor", url: "/images/partners/tripadvisor-logo.webp", height: "h-5", link: "https://www.tripadvisor.com" },
];

export default function Footer() {
  const { language } = useLanguage();

  const content = language === "id" ? {
    tagline: "Luxury Living Since 2026",
    description: "Menghadirkan pengalaman luxury co-living premium dengan standar hotel bintang 5 di jantung Denpasar, Bali. Your sanctuary in paradise.",
    contactInfo: "Informasi Kontak",
    quickLinks: "Tautan Cepat",
    connect: "Hubungi KinKen",
    followUs: "Ikuti perjalanan kami dalam mendefinisikan ulang luxury living di Bali.",
    bookWhatsApp: "Pesan via WhatsApp",
    trustedPlatforms: "Platform Booking Terpercaya",
    backToTop: "Kembali ke Atas",
    links: [
      { label: "Beranda", href: "/" },
      { label: "Tentang KinKen", href: "/about" },
      { label: "Kamar & Suite", href: "/rooms" },
      { label: "Fasilitas", href: "/facilities" },
      { label: "Galeri", href: "/gallery" },
      { label: "Lokasi", href: "/location" },
      { label: "Ulasan Tamu", href: "/testimonials" },
      { label: "Pusat FAQ", href: "/faq" },
      { label: "Hubungi Kami", href: "/contact" },
      { label: "Karir", href: "/career" },
    ],
  } : {
    tagline: "Luxury Living Since 2026",
    description: "Delivering a premium luxury co-living experience with 5-star hotel standards in the heart of Denpasar, Bali. Your sanctuary in paradise.",
    contactInfo: "Contact Information",
    quickLinks: "Quick Links",
    connect: "Connect With KinKen",
    followUs: "Follow our journey in redefining luxury living in Bali.",
    bookWhatsApp: "Book via WhatsApp",
    trustedPlatforms: "Trusted Booking Platforms",
    backToTop: "Back to Top",
    links: [
      { label: "Home", href: "/" },
      { label: "About KinKen", href: "/about" },
      { label: "Rooms & Suites", href: "/rooms" },
      { label: "Facilities", href: "/facilities" },
      { label: "Gallery", href: "/gallery" },
      { label: "Location", href: "/location" },
      { label: "Guest Reviews", href: "/testimonials" },
      { label: "FAQ Center", href: "/faq" },
      { label: "Contact Us", href: "/contact" },
      { label: "Career", href: "/career" },
    ],
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="bg-[#121212] text-white pt-20 pb-10 border-t border-white/5 relative overflow-hidden font-sans">
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: "radial-gradient(#ffffff 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      ></div>

      <div className="container px-6 md:px-12 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 mb-16">
          {/* BRAND */}
          <div className="space-y-6">
            <Link href="/" className="inline-block group">
              <div>
                <h3 className="font-serif text-4xl tracking-wide font-bold text-white group-hover:text-brand-gold transition-colors duration-300">
                  KINKEN
                </h3>
                <p className="font-sans text-xs uppercase tracking-[0.3em] text-gray-400 mt-2 group-hover:text-white transition-colors duration-300">
                  Luxury Living Since 2026
                </p>
              </div>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed font-light max-w-xs">
              {content.description}
            </p>
          </div>

          {/* CONTACT */}
          <div className="space-y-6">
            <h4 className="text-brand-gold text-xs font-bold tracking-[0.2em] uppercase mb-6">
              {content.contactInfo}
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-4 group cursor-pointer">
              <HiLocationMarker className="text-gray-500 group-hover:text-brand-gold transition-colors mt-1" size={18} />
                <div>
                  <span className="text-gray-400 text-sm leading-relaxed group-hover:text-white transition-colors block">
                    Gg. Mawar No.8, Sidakarya
                  </span>
                  <span className="text-gray-500 text-xs mt-1 block group-hover:text-gray-400 transition-colors">
                    Denpasar Selatan, Bali 80224
                  </span>
                </div>
              </li>
              <li className="flex items-center gap-4 group cursor-pointer">
                <HiPhone className="text-gray-500 group-hover:text-brand-gold transition-colors" size={18} />
                <div>
                  <span className="text-gray-400 text-sm group-hover:text-white transition-colors block">
                    +62 819 1640 1702
                  </span>
                  <span className="text-gray-500 text-xs mt-1 block group-hover:text-gray-400 transition-colors">
                    WhatsApp • Concierge • 24/7
                  </span>
                </div>
              </li>
              <li className="flex items-center gap-4 group cursor-pointer">
                <HiMail className="text-gray-500 group-hover:text-brand-gold transition-colors" size={18} />
                <div>
                  <span className="text-gray-400 text-sm group-hover:text-white transition-colors block">
                    concierge@kinken-kost.com
                  </span>
                  <span className="text-gray-500 text-xs mt-1 block group-hover:text-gray-400 transition-colors">
                    reservations@kinken-kost.com
                  </span>
                </div>
              </li>
            </ul>
          </div>

          {/* NAVIGATION */}
          <div className="space-y-6">
            <h4 className="text-brand-gold text-xs font-bold tracking-[0.2em] uppercase mb-6">
              {content.quickLinks}
            </h4>
            <ul className="space-y-3">
              {content.links.map((item) => (
                <li key={item.label}>
                  <Link
                    href={item.href}
                    className="text-gray-400 text-sm hover:text-brand-gold hover:pl-2 transition-all duration-300 inline-block font-light"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* SOCIAL & BOOKING */}
          <div className="space-y-6">
            <h4 className="text-brand-gold text-xs font-bold tracking-[0.2em] uppercase mb-6">
              {content.connect}
            </h4>

            <div className="flex gap-3 flex-wrap">
              <a href="https://instagram.com/kinken.bali" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:bg-brand-gold hover:text-white hover:border-brand-gold transition-all" aria-label="Instagram">
                <FaInstagram size={16} />
              </a>
              <a href="https://tiktok.com/@kinken.bali" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:bg-brand-gold hover:text-white hover:border-brand-gold transition-all" aria-label="TikTok">
                <FaTiktok size={16} />
              </a>
              <a href="https://youtube.com/@kinken.bali" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:bg-brand-gold hover:text-white hover:border-brand-gold transition-all" aria-label="YouTube">
                <FaYoutube size={16} />
              </a>
              <a href="https://wa.me/6281916401702" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:bg-brand-gold hover:text-white hover:border-brand-gold transition-all" aria-label="WhatsApp">
                <FaWhatsapp size={16} />
              </a>
            </div>

            <p className="text-gray-400 text-xs mt-6 font-light">{content.followUs}</p>

            <a
              href="https://wa.me/6281916401702"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full mt-4 bg-white text-black py-3 px-6 text-xs font-bold uppercase tracking-widest text-center hover:bg-brand-gold hover:text-white transition-colors duration-500 font-sans"
            >
              {content.bookWhatsApp}
            </a>
          </div>
        </div>

        {/* PARTNERS */}
        <div className="border-t border-white/10 py-8 mb-8">
          <p className="text-center text-brand-gold text-[10px] font-bold uppercase tracking-[0.2em] mb-6 opacity-70">
            {content.trustedPlatforms}
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
            {PARTNERS.map((partner) => (
              <a 
                key={partner.name} 
                href={partner.link}
                target="_blank"
                rel="noopener noreferrer"
                className="relative h-8 flex items-center group transition-all duration-300 opacity-100 md:opacity-50 hover:opacity-100"
              >
                <img
                  src={partner.url}
                  alt={partner.name}
                  className={`${partner.height} w-auto object-contain transition-all duration-300
                    brightness-0 invert
                    group-hover:brightness-100 group-hover:invert-0
                  `}
                  loading="lazy"
                />
              </a>
            ))}
          </div>
        </div>

        <div className="w-full h-[1px] bg-white/10 mb-8" />

        {/* BOTTOM */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div>
            <p className="text-gray-500 text-xs font-light">
              © 2026 KinKen Kost Elite. All rights reserved.
            </p>
            <p className="text-gray-600 text-[10px] font-light mt-1">
              PT. KinKen Luxury Living • Denpasar, Bali
            </p>
          </div>

          <div className="flex gap-8">
            <Link href="/privacy-policy" className="text-gray-500 text-xs hover:text-white transition-colors font-light">
              Privacy Policy
            </Link>
            <Link href="/terms-of-service" className="text-gray-500 text-xs hover:text-white transition-colors font-light">
              Terms of Service
            </Link>
            <Link href="/sitemap" className="text-gray-500 text-xs hover:text-white transition-colors font-light">
              Sitemap
            </Link>
          </div>

          <button
            onClick={scrollToTop}
            className="group flex items-center gap-2 text-gray-500 hover:text-brand-gold transition-colors text-xs uppercase font-bold tracking-widest cursor-pointer font-sans"
            aria-label="Back to top"
          >
            {content.backToTop}
            <span className="p-2 bg-white/5 rounded-full group-hover:bg-brand-gold group-hover:text-white transition-all">
              <HiArrowUp size={14} />
            </span>
          </button>
        </div>
      </div>
    </footer>
  );
}
